/* eslint-disable no-console */
import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import {
    Container,
    Box,
    Typography,
    Tooltip,
    IconButton,
    Switch,
    Button,
    FormControlLabel,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Add, EditOutlined } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ADMINISTRATION_USERS, MY_ORGANIZATION } from '../../configs/routes';
import { formatDate, extractErrorMessage } from '../../lib/utils';
import { useDebounce } from '../../hooks/useDebounce';
import TablePagination from '../TablePagination';
import DialogLoader from '../common/DialogLoader';
import api from '../../apis/api';

// Lazy load the dialog components
const EditUserDialog = lazy(() =>
    import('./Dialogs/EditUser').then((module) => ({
        default: module.EditUserDialog,
    })),
);

const ToggleUserDialog = lazy(() =>
    import('./Dialogs/ToggleUser').then((module) => ({
        default: module.ToggleUserDialog,
    })),
);

const AddUserDialog = lazy(() =>
    import('./Dialogs/AddUser').then((module) => ({
        default: module.AddUserDialog,
    })),
);

const Administration = () => {
    const [userList, setUserList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 5,
        page: 0,
    });
    const [search, setSearch] = useState('');
    const [totalCount, setTotalCount] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [organizationList, setOrganizationList] = useState([]);

    // Dialog states
    const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
    const [toggleUserStatusDialogOpen, setToggleUserStatusDialogOpen] = useState(false);
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);

    const debounceSearch = useDebounce(search);
    const prevSearchRef = useRef(debounceSearch);

    useEffect(() => {
        const isSearchChange = prevSearchRef.current !== debounceSearch;
        if (isSearchChange) {
            prevSearchRef.current = debounceSearch;
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            getUsersHandler(1, paginationModel.pageSize);
        } else getUsersHandler();
    }, [paginationModel, debounceSearch]);

    useEffect(() => {
        fetchMyOrganization();
    }, []);

    const fetchMyOrganization = async () => {
        try {
            const response = await api.get(MY_ORGANIZATION);
            setOrganizationList(response?.data || null);
        } catch (error) {
            console.error(error);
            toast.error(extractErrorMessage(error, 'Failed to load organization'));
        }
    };

    const getUsersHandler = async (overridePage, overridePageSize) => {
        const page = overridePage ?? paginationModel.page + 1;
        const pageSize = overridePageSize ?? paginationModel.pageSize;
        try {
            setLoading(true);
            const response = await api.get(buildUsersUrlWithQuery(page, pageSize, debounceSearch));
            setUserList(response?.data?.results || []);
            setTotalCount(response?.data?.count || 0);
        } catch (error) {
            console.error(error);
            toast.error(extractErrorMessage(error, 'Failed to load users'));
        } finally {
            setLoading(false);
        }
    };

    const buildUsersUrlWithQuery = (page, pageSize, search) => {
        const params = new URLSearchParams({
            page: String(page),
            page_size: String(pageSize),
            ...(search && { search }),
        });
        return `${ADMINISTRATION_USERS}?${params.toString()}`;
    };

    const handleEditUser = (user) => {
        setSelectedUser(user);
        setEditUserDialogOpen(true);
    };

    const handleToggleUserStatus = (user) => {
        setSelectedUser(user);
        setToggleUserStatusDialogOpen(true);
    };

    return (
        <Container className="custom_table_container" maxWidth={false}>
            <Box variant="div" className="table_filters">
                <Typography variant="h6" fontWeight={600}>
                    User Management
                </Typography>
                <Box className="filter_box">
                    <TextField
                        size="small"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <span className="material-symbols-rounded">search</span>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={() => setAddUserDialogOpen(true)}
                    >
                        Add User
                    </Button>
                </Box>
            </Box>

            <Box variant="div" className="custom_table_responsive">
                <Table
                    userList={userList}
                    loading={loading}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    totalCount={totalCount}
                    handleEditUser={handleEditUser}
                    handleToggleUserStatus={handleToggleUserStatus}
                />
            </Box>

            {editUserDialogOpen && (
                <Suspense fallback={<DialogLoader />}>
                    <EditUserDialog
                        open={editUserDialogOpen}
                        onClose={() => setEditUserDialogOpen(false)}
                        onSuccess={getUsersHandler}
                        selectedUser={selectedUser}
                        organizationList={organizationList}
                    />
                </Suspense>
            )}

            {toggleUserStatusDialogOpen && (
                <Suspense fallback={<DialogLoader />}>
                    <ToggleUserDialog
                        open={toggleUserStatusDialogOpen}
                        onClose={() => setToggleUserStatusDialogOpen(false)}
                        onSuccess={getUsersHandler}
                        selectedUser={selectedUser}
                    />
                </Suspense>
            )}

            {addUserDialogOpen && (
                <Suspense fallback={<DialogLoader />}>
                    <AddUserDialog
                        open={addUserDialogOpen}
                        onClose={() => setAddUserDialogOpen(false)}
                        onSuccess={getUsersHandler}
                        organizationList={organizationList}
                    />
                </Suspense>
            )}
        </Container>
    );
};

export default Administration;

const Table = ({
    userList,
    loading,
    paginationModel,
    setPaginationModel,
    totalCount,
    handleEditUser,
    handleToggleUserStatus,
}) => {
    const currentUserEmail = useSelector((state) => state.auth.user?.email);

    const userListColumns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 2,
            renderCell: (params) => `${params?.row?.first_name || ''} ${params?.row?.last_name || ''}`.trim() || '-',
        },
        {
            field: 'email',
            headerName: 'Email',
            flex: 2.5,
            renderCell: (params) => (
                <Tooltip title={params?.value} placement="top" arrow>
                    <Box className="teams-apps-cell-text">{params?.row?.email || '-'}</Box>
                </Tooltip>
            ),
        },
        {
            field: 'role',
            headerName: 'Role',
            flex: 1,
            renderCell: (params) => capitalize(params?.row?.role) || '-',
        },
        {
            field: 'is_email_verified   ',
            headerName: 'Email Verified',
            flex: 1,
            renderCell: (params) => (params?.row?.is_email_verified ? 'Yes' : 'No'),
        },
        {
            field: 'is_active',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params?.row?.is_active ? 'Active' : 'Inactive'} placement="top" arrow>
                    <span>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={params?.row?.is_active}
                                    onChange={() => handleToggleUserStatus(params?.row)}
                                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                                    size="small"
                                    disabled={params?.row?.email === currentUserEmail}
                                />
                            }
                            label={params?.row?.is_active ? 'Active' : 'Inactive'}
                            disabled={params?.row?.email === currentUserEmail}
                            sx={{
                                '& .MuiFormControlLabel-label.Mui-disabled': {
                                    color: 'inherit',
                                },
                            }}
                        />
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'updated_at',
            headerName: 'Modified On',
            flex: 1,
            renderCell: (params) => formatDate(params?.row?.updated_at) || '-',
        },
        {
            field: 'updated_by',
            headerName: 'Modified By',
            flex: 1.5,
            renderCell: (params) => params?.row?.updated_by?.name || '-',
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box className="teams-apps-actions-cell">
                        <Tooltip title={'Edit'} placement="top" arrow>
                            <span>
                                <IconButton
                                    onClick={() => handleEditUser(params?.row)}
                                    color="primary"
                                    aria-label={`Edit user ${params?.row?.firstName + ' ' + params?.row?.lastName || 'user'}`}
                                >
                                    <EditOutlined fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <Box variant="div" className="custom_table_responsive">
            <DataGrid
                rows={userList || []}
                columns={userListColumns}
                getRowId={(row) => row.id}
                autoHeight
                loading={loading}
                disableRowSelectionOnClick
                disableColumnMenu
                localeText={{
                    noRowsLabel: 'No Users Found',
                    sortLabel: '',
                }}
                rowCount={totalCount}
                paginationMode="server"
                pagination
                pageSizeOptions={[5, 10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                slots={{
                    pagination: () => (
                        <TablePagination
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            totalCount={totalCount}
                            pageSizeOptions={[5, 10, 25, 50]}
                        />
                    ),
                }}
            />
        </Box>
    );
};
